import React, { useEffect, useMemo, useState } from 'react';
import { fetchInboxThread, type InboxThreadMessage } from '../../lib/inboxApi';
import { formatDateTime as fmtDateTime } from '../../utils/dateFormat';

/**
 * The rest of the conversation, above the message you opened.
 *
 * The inbox lists one row per CONVERSATION now, and the row opens its most recent message. This
 * is how you get at the others: every message in the thread, oldest first, collapsed to one
 * line each, expanding in place.
 *
 * Deliberately additive rather than a rewrite of EmailDetail into a chat pane. EmailDetail
 * already renders one message properly — sanitised HTML, the HTML/Text toggle, attachment
 * previews, reclassify, delete — and none of that behaviour needed to change to make the
 * conversation readable. Reusing it for N messages would have meant restructuring a working
 * 400-line component for a feature that only needs the earlier messages to be reachable.
 *
 * ponytail: no shared cache, no prefetch. One request per conversation you actually open, and
 * it only fires for threads that have more than one message.
 */

interface Props {
  threadId?: string;
  /** From the list row. Used to skip the fetch entirely on a single-message conversation. */
  messageCount?: number;
  /** The message EmailDetail is showing below, so it is marked rather than repeated. */
  currentMessageId?: string;
}

const formatWhen = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return fmtDateTime(d);
};

function cleanSender(from?: string) {
  const raw = String(from || '').trim();
  if (!raw) return 'Unknown sender';
  const name = raw.split('<')[0].replace(/["']/g, '').trim();
  return name || raw;
}

/** A one-line preview. The stored bodyText already has the HTML flattened at ingest. */
function snippet(msg: InboxThreadMessage) {
  const text = String(msg.bodyText || '').replace(/\s+/g, ' ').trim();
  return text.slice(0, 140);
}

export const ThreadTimeline: React.FC<Props> = ({ threadId, messageCount, currentMessageId }) => {
  const [messages, setMessages] = useState<InboxThreadMessage[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState(false);

  const hasThread = !!threadId && (messageCount ?? 1) > 1;

  useEffect(() => {
    // Reset when the user moves to another conversation, or this would briefly show the
    // previous thread's messages under the new subject.
    setMessages(null);
    setExpanded({});
    setError(null);
    setOpen(false);
    // A fetch for the previous conversation is now stale and will never clear this itself.
    setLoading(false);
  }, [threadId]);

  // One automatic attempt per conversation, remembered by threadId in a ref and cleared when the
  // user moves to another conversation. `messages` and `loading` must NOT be in the guard or the
  // deps (D114.1): setLoading(true) re-ran the effect, its cleanup cancelled the request in
  // flight, the re-run bailed at the guard, and "Loading the conversation" never cleared. (The
  // older `messages || loading` guard also refetched forever after a failure.) Staleness is
  // checked against the ref instead of a per-run flag, so hiding the panel mid-fetch no longer
  // drops the result, and a result for a conversation the user left is ignored.
  const attemptedFor = React.useRef<string | null>(null);
  useEffect(() => {
    attemptedFor.current = null;
  }, [threadId]);
  useEffect(() => {
    if (!open || !hasThread) return;
    if (attemptedFor.current === threadId) return;
    const id = threadId as string;
    attemptedFor.current = id;
    const current = () => attemptedFor.current === id;
    setLoading(true);
    fetchInboxThread(id)
      .then((res) => {
        if (current()) setMessages(res.messages || []);
      })
      .catch((e: any) => {
        if (current()) setError(e?.message || 'Failed to load the conversation');
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
  }, [open, hasThread, threadId]);

  // The message shown below this component is not repeated inside it.
  const earlier = useMemo(
    () => (messages || []).filter((m) => String(m._id) !== String(currentMessageId || '')),
    [messages, currentMessageId],
  );

  if (!hasThread) return null;

  const count = messageCount ?? 1;

  return (
    <div className="mb-4 rounded border border-slate-200 bg-[#f7faf8]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-xs font-semibold text-slate-700">
          Conversation · {count} message{count === 1 ? '' : 's'}
        </span>
        <span className="text-xs font-semibold text-primary-500">
          {open ? 'Hide earlier messages' : 'Show earlier messages'}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-200 px-3 py-2">
          {loading && <div className="py-2 text-xs text-slate-500">Loading the conversation…</div>}
          {error && <div className="py-2 text-xs text-rose-600">{error}</div>}
          {!loading && !error && earlier.length === 0 && (
            <div className="py-2 text-xs text-slate-500">
              No earlier messages — this conversation has only the message shown below.
            </div>
          )}

          <div className="space-y-1">
            {earlier.map((msg) => {
              const id = String(msg._id);
              const isOpen = !!expanded[id];
              const outbound = msg.direction === 'outbound';
              return (
                <div key={id} className="rounded border border-slate-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
                    className="flex w-full items-start justify-between gap-2 px-2 py-1.5 text-left hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-xs font-semibold text-slate-800">
                          {outbound ? 'You' : cleanSender(msg.from)}
                        </span>
                        {outbound && (
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-xs font-semibold text-emerald-700">
                            Sent
                          </span>
                        )}
                        {!!msg.attachments?.length && (
                          <span className="text-xs text-slate-400" title={`${msg.attachments.length} attachment(s)`}>
                            📎 {msg.attachments.length}
                          </span>
                        )}
                      </div>
                      {!isOpen && <div className="truncate text-xs text-slate-500">{snippet(msg)}</div>}
                    </div>
                    <span className="flex-shrink-0 whitespace-nowrap text-xs text-slate-400">
                      {formatWhen(msg.receivedAt || msg.date)}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 px-2 py-2">
                      <div className="mb-1 text-xs text-slate-500">
                        <span className="font-semibold">To:</span> {msg.to || '—'}
                        {msg.cc ? (
                          <>
                            <span className="mx-1">|</span>
                            <span className="font-semibold">CC:</span> {msg.cc}
                          </>
                        ) : null}
                      </div>
                      {/*
                        bodyText, not bodyHtml, on purpose. The stored HTML would need the same
                        sanitising EmailDetail does before it could be injected, and duplicating
                        that here would mean two sanitisers to keep in step — the one place a
                        missed rule becomes stored XSS. The plain text is what a preview needs;
                        the full message is one click away in its own row.
                      */}
                      <div className="whitespace-pre-line text-xs leading-relaxed text-slate-700">
                        {String(msg.bodyText || '').trim() || 'No plain-text body stored for this message.'}
                      </div>
                      {!!msg.attachments?.length && (
                        <div className="mt-2 text-xs text-slate-500">
                          <span className="font-semibold">Attachments:</span>{' '}
                          {msg.attachments.map((a) => a.filename).filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
